import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Megaphone, Send } from "lucide-react";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";

interface SendGlobalNotificationModalProps {
  open: boolean;
  onClose: () => void;
}

export const SendGlobalNotificationModal = ({
  open,
  onClose
}: SendGlobalNotificationModalProps) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const { sendBroadcast, isSending } = useAdminNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !message.trim() || !confirmed) {
      return;
    }

    try {
      await sendBroadcast.mutateAsync({
        title: title.trim(),
        message: message.trim()
      });

      setTitle("");
      setMessage("");
      setConfirmed(false);
      onClose();
    } catch (error) {
      // Error handled by hook
    }
  };

  const isValid = title.trim().length > 0 && message.trim().length > 0 && confirmed;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-orange-500" />
            Enviar Notificación Global
          </DialogTitle>
          <DialogDescription>
            Esta notificación se enviará a <strong>TODOS los usuarios registrados</strong> en la plataforma.
            Usa esta función con precaución.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Alert variant="destructive" className="bg-orange-50 border-orange-200">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>⚠️ ADVERTENCIA:</strong> Esta acción enviará una notificación a todos los usuarios activos.
              Asegúrate de revisar el contenido antes de enviar.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="broadcast-title">Título *</Label>
            <Input
              id="broadcast-title"
              placeholder="Ej: Mantenimiento programado del sistema"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
            <p className="text-sm text-muted-foreground text-right">
              {title.length}/100 caracteres
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="broadcast-message">Mensaje *</Label>
            <Textarea
              id="broadcast-message"
              placeholder="Escribe el mensaje que verán todos los usuarios..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={6}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground text-right">
              {message.length}/500 caracteres
            </p>
          </div>

          {title && message && (
            <div className="border rounded-lg p-4 bg-muted/50">
              <p className="text-sm font-medium mb-2">Vista previa:</p>
              <div className="bg-background p-3 rounded border">
                <p className="font-semibold text-sm">{title}</p>
                <p className="text-sm text-muted-foreground mt-1">{message}</p>
              </div>
            </div>
          )}

          <div className="flex items-start space-x-3 border rounded-lg p-4 bg-yellow-50 dark:bg-yellow-900/20">
            <Checkbox
              id="broadcast-confirm"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked as boolean)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="broadcast-confirm"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Confirmo que deseo enviar esta notificación a todos los usuarios
              </Label>
              <p className="text-sm text-muted-foreground">
                Esta acción no se puede deshacer. La notificación será enviada inmediatamente.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!isValid || isSending}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Send className="h-4 w-4 mr-2" />
              {isSending ? "Enviando..." : "Enviar Notificación Global"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
